import * as React from "react";
import { Outlet } from "react-router-dom";
import { useDeviceTier } from "../../hooks/use-device-tier";
import { useOnlineStatus } from "../../hooks/use-online-status";
import { OfflineBanner } from "../patterns/states";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../ui/sidebar";
import { TooltipProvider } from "../ui/tooltip";
import { NotificationInbox } from "../notifications/notification-inbox";
import { AppSidebar } from "./app-sidebar";
import mark from "./claymore-thistle-mark.png";
import { useCurrentUser } from "../../backend/queries/auth";
import { ChatDock, type DockState } from "../../scenes/chat/ChatDock";

export type AppShellProps = {
  /** Defaults to the routed child, so the shell can be a layout route. */
  children?: React.ReactNode;
  /**
   * A contextual strip under the offline notice, for states that apply to the
   * whole screen such as an agent previewing the client view.
   */
  banner?: React.ReactNode;
  productName?: string;
};

/**
 * The one application shell.
 *
 * - Desktop (`lg` and up) shows the full navigation rail.
 * - Tablet (`md` to `lg`) collapses it to icons to protect content width.
 * - Mobile (below `md`) moves it into a drawer behind a header button.
 *
 * The rail is present at every size and its contents come from one role-aware
 * model, so navigation does not move or change shape as a screen loads.
 */
export function AppShell({ children, banner, productName }: AppShellProps) {
  const tier = useDeviceTier();
  const online = useOnlineStatus();
  const { data: user } = useCurrentUser();
  const [chatState, setChatState] = React.useState<DockState>("collapsed");
  const role = user?.role || user?.profile?.role;

  // The rail starts expanded on desktop and collapsed on tablet. It is
  // controlled so that crossing a breakpoint re-applies that default, while a
  // deliberate collapse inside one tier is left alone.
  const [open, setOpen] = React.useState(() => tier === "desktop");
  const previousTier = React.useRef(tier);

  React.useEffect(() => {
    if (previousTier.current === tier) return;
    previousTier.current = tier;
    setOpen(tier === "desktop");
  }, [tier]);

  return (
    <TooltipProvider>
      <SidebarProvider open={open} onOpenChange={setOpen}>
        <a className="skip-link" href="#main-content">
          Skip to main content
        </a>

        <AppSidebar productName={productName} />

        <SidebarInset className="min-w-0">
          <OfflineBanner online={online} />
          {banner}

          {/* Shared notification access at every size; drawer controls on phones. */}
          <header className="sticky top-0 z-30 flex h-header shrink-0 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur">
            {/* Keep phone navigation controls comfortable to tap. */}
            <SidebarTrigger className="min-h-touch min-w-touch md:hidden" />
            <img
              src={mark}
              alt=""
              aria-hidden="true"
              className="h-5 w-5 shrink-0 object-contain dark:invert md:hidden"
            />
            <span className="truncate text-sm font-semibold md:hidden">
              {productName ?? "Claymore Thistle"}
            </span>
            <div className="ml-auto">
              {user?.id && <NotificationInbox key={user.id} userId={user.id} />}
            </div>
          </header>

          {/*
            The skip link targets this element. `tabIndex={-1}` lets it receive
            focus so the next Tab continues inside the page content.
          */}
          <main
            id="main-content"
            tabIndex={-1}
            className="min-w-0 flex-1 focus-visible:outline-none focus-visible:ring-0"
          >
            {children ?? <Outlet />}
          </main>

          <footer className="px-4 py-4 text-center text-2xs text-muted-foreground sm:px-6 lg:px-8">
            &copy; Claymore Thistle {new Date().getFullYear()}
          </footer>
        </SidebarInset>
        {role === "Client" ? (
          <ChatDock
            currentUserId={user?.id}
            state={chatState}
            onStateChange={setChatState}
          />
        ) : null}
      </SidebarProvider>
    </TooltipProvider>
  );
}
